import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, ArrowUpTrayIcon, Cog6ToothIcon, FilmIcon } from '@heroicons/react/24/outline';
import { useOssDirectUpload } from '../../hooks/useOssDirectUpload';
import { IImageGeneration, IUserModel, IProjectAsset, IVideoClipResource } from '@packages/global';
import ImageSelector from './ImageSelector';
import EpisodeImageSelector from './EpisodeImageSelector';
import { ProjectAssetSelector } from '../projectAssets';

interface ImageUploadProps {
  onUploadSuccess: (url: string, metadata?: { prompt?: string; negativePrompt?: string }) => void;
  className?: string;
  models?: IUserModel[];
  mode?: 'default' | 'button';
  buttonClassName?: string;
  buttonText?: string;
  projectId?: string;
  episodeId?: string;
  compact?: boolean;
  showAssets?: boolean;
}

export default function ImageUpload({ onUploadSuccess, className = '', models = [], mode = 'default', buttonClassName, buttonText = '上传图片', projectId, episodeId, compact = false, showAssets = false }: ImageUploadProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const { uploadFile, uploading } = useOssDirectUpload();
  const [error, setError] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [showEpisodeSelector, setShowEpisodeSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedImageTypes.includes(file.type)) {
      setError(t('common_ui:upload.image.invalidType'));
      return;
    }

    // 验证文件大小 (20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError(t('common_ui:upload.image.sizeLimit', { size: 20 }));
      return;
    }

    setError(null);

    try {
      const url = await uploadFile(file);
      onUploadSuccess(url);
    } catch (err: any) {
      setError(err.message || t('common_ui:upload.image.failed'));
    } finally {
      // 清空文件输入，允许重新选择同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageSelect = (image: IImageGeneration) => {
    if (image.resultImageUrl) {
      onUploadSuccess(image.resultImageUrl, {
        prompt: image.prompt,
        negativePrompt: image.negativePrompt
      });
    }
  };

  const handleAssetSelect = (asset: IProjectAsset, imageUrl: string) => {
    onUploadSuccess(imageUrl);
    setShowAssetSelector(false);
  };

  const handleEpisodeImageSelect = (imageUrl: string, _resource: IVideoClipResource) => {
    onUploadSuccess(imageUrl);
    setShowEpisodeSelector(false);
  };

  return (
    <div className={`relative ${className}`}>
      {compact ? (
        // Compact mode - small toolbar button
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleClick}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <PhotoIcon className="w-3.5 h-3.5 text-green-400" />
            )}
            <span>{t('common_ui:upload.image.refImage')}</span>
          </button>
          {error && (
            <p className="absolute top-full left-0 mt-1 text-xs text-red-400 whitespace-nowrap">{error}</p>
          )}
        </>
      ) : mode === 'button' ? (
        // Button mode - single button only
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleClick}
            disabled={uploading}
            className={buttonClassName || "px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"}
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <ArrowUpTrayIcon className="h-4 w-4" />
            )}
            {uploading ? t('common_ui:upload.image.uploading') : buttonText}
          </button>
          {error && (
            <p className="mt-1 text-sm text-red-400">{error}</p>
          )}
        </>
      ) : (
        // Default mode - original layout
        <div className='border-2 border-dashed border-border-default rounded-md'>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            type="button"
            onClick={handleClick}
            disabled={uploading}
            className="w-full h-12 flex flex-col items-center justify-center text-gray-400 hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            ) : (
              <>
                <ArrowUpTrayIcon className="h-8 w-8 mt-2" />
              </>
            )}
          </button>
          <div className='flex items-center justify-center gap-2'>
            <button
              type="button"
              className='mt-1 mb-2 px-2 bg-green-700/50 text-white rounded-md hover:bg-green-600/50 transition-colors flex items-center gap-1'
              onClick={() => setShowSelector(true)}
              disabled={uploading}
            >
              <PhotoIcon className="w-3 h-3" />
              <span className="text-xs">{t('common_ui:upload.image.gallery')}</span>
            </button>
            {episodeId && (
              <button
                type="button"
                className='mt-1 mb-2 px-2 bg-blue-700/50 text-white rounded-md hover:bg-blue-600/50 transition-colors flex items-center gap-1'
                onClick={() => setShowEpisodeSelector(true)}
                disabled={uploading}
              >
                <FilmIcon className="w-3 h-3" />
                <span className="text-xs">{t('common_ui:upload.image.resource')}</span>
              </button>
            )}

            {(showAssets || projectId) && (
              <button
                type="button"
                className='mt-1 mb-2 px-2 bg-purple-700/50 text-white rounded-md hover:bg-purple-600/50 transition-colors flex items-center gap-1'
                onClick={() => setShowAssetSelector(true)}
                disabled={uploading}
              >
                <Cog6ToothIcon className="w-3 h-3" />
                <span className="text-xs">{t('common_ui:upload.image.settings')}</span>
              </button>
            )}
          </div>
          {error && (
            <p className="mt-1 text-sm text-red-400">{error}</p>
          )}

          <ImageSelector
            isOpen={showSelector}
            onClose={() => setShowSelector(false)}
            onSelect={handleImageSelect}
            models={models}
          />

          {episodeId && (
            <EpisodeImageSelector
              isOpen={showEpisodeSelector}
              onClose={() => setShowEpisodeSelector(false)}
              onSelect={handleEpisodeImageSelect}
              episodeId={episodeId}
            />
          )}

          {(showAssets || projectId) && (
            <ProjectAssetSelector
              isOpen={showAssetSelector}
              episodeId={episodeId}
              projectId={projectId}
              onSelect={handleAssetSelect}
              onClose={() => setShowAssetSelector(false)}
              initialAspect="image"
            />
          )}
        </div>
      )}
    </div>
  );
}
